import {CompanyEntity} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {useContainerExportForm} from "./useContainerExportForm";
import {CompanyMultiSelect} from "../../select/entity/CompanySelect/CompanyMultiSelect";

export interface ContainerExportFormProps {
  options?: ContainerExportFormOptions;
  defaultValues?: ContainerExportFormDefaultValues;
  onSubmit?: (values: ContainerExportFormValues) => void;
  submitting?: boolean;
}

export interface ContainerExportFormOptions {
  regionId?: string
}

export interface ContainerExportFormValues {
  companies?: (CompanyEntity | string)[];
  wellMappingKey?: string;
  containerMappingKey?: string;
  containerMappingKey2?: string;
}

export interface ContainerExportFormDefaultValues extends Partial<ContainerExportFormValues> {

}

export interface ContainerExportFormRawValues {
  companies?: string[];
  wellMappingKey?: string
  containerMappingKey?: string
  containerMappingKey2?: string;
}

export type ContainerExportFormType = UseFormReturnType<ContainerExportFormRawValues, (values: ContainerExportFormRawValues) => ContainerExportFormRawValues>

export const ContainerExportForm = (props: ContainerExportFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useContainerExportForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        companies: values.companies,
        wellMappingKey: values.wellMappingKey,
        containerMappingKey: values.containerMappingKey,
        containerMappingKey2: values.containerMappingKey2,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <CompanyMultiSelect
            label={t("common.entity.container.export.form.field.companies", "Companies")}
            placeholder={t<string>("common.entity.container.export.form.field.companies.placeholder", "Companies")}
            selector={{
              regionId: options?.regionId,
            }}
            clearable
            {...form.getInputProps("companies")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.container.export.form.field.wellmapping", "Well Mapping Key")}
            placeholder={t<string>("common.entity.container.export.form.field.wellmapping", "Well Mapping Key")}
            {...form.getInputProps("wellMappingKey")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.container.export.form.field.containermapping", "Container Mapping Key")}
            placeholder={t<string>("common.entity.container.export.form.field.containermapping", "Container Mapping Key")}
            {...form.getInputProps("containerMappingKey")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.container.export.form.field.containermapping2", "Container Mapping Key 2")}
            placeholder={t<string>("common.entity.container.export.form.field.containermapping2", "Container Mapping Key 2")}
            {...form.getInputProps("containerMappingKey2")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface ContainerExportFormModalProps extends ContainerExportFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ContainerExportFormModal = (props: ContainerExportFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ContainerExportForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
